import { createFileRoute } from "@tanstack/react-router";
import { type ChangeEventHandler, useRef, useState } from "react";
import toast from "react-hot-toast";
import Download from "~icons/material-symbols/download";
import Upload from "~icons/material-symbols/upload";
import { Code, ConnectError } from "@connectrpc/connect";
import {
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError,
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import { css } from "../../../../../styled-system/css";
import { LoadingOverlay } from "../../../../components/loading-overlay";
import { useCreateHokkaidoR8HouseholderCodeUsesAsPaperVoucher } from "../../../../libs/admin/hooks";
import { downloadCsvFile } from "../../../../libs/utils/csv";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import {
  pageTitle,
  sectionTitle,
  sectionWrapper,
  titleWrapper,
} from "../../../../styles/sectionStyles";
import { QR_CODE_COLUMN, type ParseResult, parseCsv } from "./-csv/parser";

export const Route = createFileRoute("/admin/hokkaido/paper-vouchers/")({
  component: RouteComponent,
});

// Example rows show the expected cell format; the serial numbers are placeholders.
const SAMPLE_CSV_CONTENT = `${[
  QR_CODE_COLUMN,
  "https://p8n.jp/i/xxxxxxxxxxxxxxxxxxxxxx?u=%2Fhkp%2F234ABCDEFGHJ&t=h",
  "https://p8n.jp/i/xxxxxxxxxxxxxxxxxxxxxx?u=%2Fhkp%2F234KMNPQRSTV&t=h",
].join("\n")}\n`;

type RowDisplayError = {
  row: number;
  message: string;
};

type SubmitResult =
  | { kind: "idle" }
  | { kind: "success"; count: number }
  | { kind: "error"; errors: RowDisplayError[] }
  | { kind: "fatal"; message: string };

const formatReason = (
  reason: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason,
): string => {
  switch (reason) {
    case CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason.MALFORMED_CODE:
      return "シリアルナンバーの形式が正しくありません";
    case CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason.DUPLICATE_IN_REQUEST:
      return "CSV 内で重複しているシリアルナンバーです";
    case CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason.CODE_NOT_FOUND:
      return "登録されていないシリアルナンバーです";
    case CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason.ALREADY_USED_AS_WALLET_BALANCE:
      return "既にポイントで受け取り済みのシリアルナンバーです";
    case CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason.ALREADY_USED_AS_PAPER_VOUCHER:
      return "既にギフト券交付済みのシリアルナンバーです";
    default:
      return "不明なエラー";
  }
};

const fatalMessageFromConnect = (error: unknown): string => {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied) {
      return "権限がありません。組織を確認してください。";
    }
    return error.message;
  }
  return error instanceof Error
    ? error.message
    : "通信に失敗しました。時間を置いて再度お試しください。";
};

function RouteComponent() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResult>({ kind: "idle" });

  const mutation = useCreateHokkaidoR8HouseholderCodeUsesAsPaperVoucher();

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const result = await parseCsv(text);
      setParseResult(result);
      setFileName(file.name);
      setSubmitResult({ kind: "idle" });
      if (result.errors.length === 0 && result.rows.length === 0) {
        toast("データ行がありません", { icon: "ℹ️" });
      }
    } catch (err) {
      console.error("CSV parsing failed", err);
      setParseResult(null);
      setSubmitResult({ kind: "idle" });
      toast.error(
        err instanceof Error ? err.message : "CSVの読み込みに失敗しました",
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadSample = () => {
    downloadCsvFile("paper_voucher_sample.csv", SAMPLE_CSV_CONTENT);
  };

  const handleRegister = async () => {
    if (!parseResult || parseResult.errors.length > 0 || parseResult.rows.length === 0) {
      return;
    }
    setSubmitResult({ kind: "idle" });
    try {
      await mutation.mutateAsync({
        codes: parseResult.rows.map((r) => r.code),
      });
      setSubmitResult({ kind: "success", count: parseResult.rows.length });
    } catch (error) {
      console.error("Failed to register paper vouchers:", error);
      if (
        error instanceof ConnectError &&
        error.code === Code.FailedPrecondition
      ) {
        const details = error.findDetails(
          CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError,
        );
        if (details.length > 0) {
          const rowErrors: RowDisplayError[] = details.map((d) => ({
            row: parseResult.rows[d.index]?.row ?? d.index + 1,
            message: formatReason(d.reason),
          }));
          rowErrors.sort((a, b) => a.row - b.row);
          setSubmitResult({ kind: "error", errors: rowErrors });
          return;
        }
      }
      setSubmitResult({ kind: "fatal", message: fatalMessageFromConnect(error) });
    }
  };

  const parseErrorsAsDisplay: RowDisplayError[] =
    parseResult?.errors.map((e) => ({ row: e.row, message: e.message })) ?? [];
  const parseRowCount = parseResult?.rows.length ?? 0;
  const canRegister =
    !!parseResult &&
    parseResult.errors.length === 0 &&
    parseRowCount > 0 &&
    !mutation.isPending &&
    submitResult.kind !== "success";

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>ギフト券交付登録</h1>
      </div>
      <div className={css({ display: "flex", flexDirection: "column", gap: "24px" })}>
        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>CSVファイルを選択</div>
          <div className={css({ display: "flex", flexDirection: "column", gap: "12px" })}>
            <p className={css({ fontSize: "14px", color: "text.secondary" })}>
              ヘッダー付き CSV を読み込みます。<code>{QR_CODE_COLUMN}</code>{" "}
              カラムに <code>https://p8n.jp/i/...?u=%2Fhkp%2F&lt;シリアルナンバー&gt;&amp;t=h</code>{" "}
              形式の URL を入れてください。その他のカラムは無視されます。1 行でも不備があると CSV
              全体が登録対象外となり、どのシリアルナンバーも交付済みになりません。
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={mutation.isPending}
            />
            <div className={css({ display: "flex", gap: "12px", flexWrap: "wrap" })}>
              <button
                type="button"
                className={button({ color: "secondary" })}
                onClick={handleDownloadSample}
              >
                <Download width="20" height="20" />
                サンプルCSVをダウンロード
              </button>
              <button
                type="button"
                className={button({ color: "secondary" })}
                onClick={() => fileInputRef.current?.click()}
                disabled={mutation.isPending}
              >
                <Upload width="20" height="20" />
                CSVファイルを読み込む
              </button>
            </div>
            {fileName && (
              <p className={css({ fontSize: "14px", color: "text.secondary" })}>
                読み込み済み: {fileName}（{parseRowCount} 件）
              </p>
            )}
          </div>
        </div>

        {parseErrorsAsDisplay.length > 0 && (
          <ErrorList title="CSV 解析時のエラー" errors={parseErrorsAsDisplay} />
        )}

        {submitResult.kind === "error" && (
          <ErrorList
            title="登録時のエラー"
            errors={submitResult.errors}
          />
        )}

        {submitResult.kind === "fatal" && (
          <div className={css({ color: "text.accentError", fontSize: "14px" })}>
            {submitResult.message}
          </div>
        )}

        {submitResult.kind === "success" && (
          <div className={css({ fontSize: "14px", color: "text.secondary" })}>
            {submitResult.count} 件のシリアルナンバーをギフト券交付済みとして登録しました。
          </div>
        )}

        <div className={css({ display: "flex", gap: "12px", alignItems: "center" })}>
          <button
            type="button"
            className={button({ color: "primary" })}
            onClick={handleRegister}
            disabled={!canRegister}
          >
            ギフト券交付登録 {parseRowCount > 0 ? `(${parseRowCount}件)` : ""}
            {mutation.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorList({ title, errors }: { title: string; errors: RowDisplayError[] }) {
  return (
    <div
      className={css({
        color: "text.accentError",
        fontSize: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      })}
    >
      <span>
        {errors.length}件のエラーがあります（{title}）
      </span>
      <ul className={css({ listStyle: "disc", pl: "2em" })}>
        {errors.map((e, i) => (
          <li key={`${e.row}-${i}`}>
            {e.row}行目: {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
